import { useEffect, useState } from 'react'
import { X, CreditCard, Loader2 } from 'lucide-react'
import api, { paymentMethodsAPI, paymentSettingsAPI, customerCardsAPI } from '../services/api'
import toast from '../utils/toast'

const today = () => new Date().toISOString().split('T')[0]

const emptyForm = () => ({
  amount: '',
  tipAmount: '',
  paymentMethod: 'cash',
  customPaymentMethod: '',
  paymentDate: today(),
  notes: '',
})

const inputClass =
  'w-full px-3 py-2 text-sm border border-[var(--sf-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)]'

// Records a manual payment against a job via /transactions/record-payment
// (same endpoint and tip math as the legacy job page). The backend creates
// the invoice if the job doesn't have one yet and updates the job's
// payment_status / total_paid_amount.
export default function CollectPaymentModal({ open, job, onClose, onRecorded }) {
  const [form, setForm] = useState(emptyForm)
  const [customMethods, setCustomMethods] = useState([])
  const [tipSettings, setTipSettings] = useState({ tipCalculationMode: 'automatic', paymentTypeFees: {} })
  const [alreadyPaid, setAlreadyPaid] = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  // Issue #70 — saved cards on file for this customer. If any exist we
  // show a "Charge saved card" section at the top so the operator can
  // one-click bill the Visa/Amex they already captured instead of
  // typing card details again.
  const [savedCards, setSavedCards] = useState([])
  const [cardsLoading, setCardsLoading] = useState(false)
  const [chargingId, setChargingId] = useState(null)

  const totalDue = parseFloat(job?.total ?? job?.total_amount ?? job?.price) || 0
  const remaining = Math.max(0, totalDue - alreadyPaid)

  useEffect(() => {
    if (!open || !job?.id) return
    let cancelled = false
    setError('')
    setForm(emptyForm())
    setSavedCards([])
    setCardsLoading(!!job.customer_id)
    Promise.all([
      paymentMethodsAPI.getPaymentMethods().catch(() => []),
      paymentSettingsAPI.getPaymentSettings().catch(() => ({})),
      api.get(`/transactions/job/${job.id}`).then((r) => r.data).catch(() => null),
      job.customer_id ? customerCardsAPI.list(job.customer_id).catch(() => []) : Promise.resolve([]),
    ]).then(([methods, settings, history, cards]) => {
      if (cancelled) return
      setCustomMethods(Array.isArray(methods) ? methods : [])
      setTipSettings({
        tipCalculationMode: settings?.tipCalculationMode || 'automatic',
        paymentTypeFees: settings?.paymentTypeFees || {},
      })
      const txs = history?.transactions || []
      const paid = history?.totalPaid ?? txs.reduce((s, tx) => s + (parseFloat(tx.amount) || 0), 0)
      const paidNum = parseFloat(paid) || parseFloat(job.total_paid_amount) || 0
      setAlreadyPaid(paidNum)
      const due = Math.max(0, totalDue - paidNum)
      setForm((f) => ({ ...f, amount: due > 0 ? due.toFixed(2) : '' }))
      setSavedCards(Array.isArray(cards) ? cards : [])
      setCardsLoading(false)
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, job?.id])

  if (!open || !job) return null

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }))
  const methodKey =
    form.paymentMethod === 'other' && form.customPaymentMethod.trim()
      ? form.customPaymentMethod.trim()
      : form.paymentMethod
  const isAutoTip = tipSettings.tipCalculationMode === 'automatic'
  const paidAmount = parseFloat(form.amount) || 0
  const feePercent = tipSettings.paymentTypeFees?.[methodKey] || 0
  const fee = paidAmount * (feePercent / 100)
  const autoTip = Math.max(0, paidAmount - remaining - fee)

  const brandLabel = (b) => {
    if (!b) return 'Card'
    const l = String(b).toLowerCase()
    if (l === 'visa') return 'Visa'
    if (l === 'mastercard') return 'Mastercard'
    if (l === 'amex') return 'Amex'
    if (l === 'discover') return 'Discover'
    return b[0].toUpperCase() + b.slice(1)
  }

  // Charge a saved card for the amount currently in the form (or the
  // remaining due if the field is empty). The backend charges the card,
  // records the payment against this job and updates invoice + job status
  // in one call.
  const handleChargeSaved = async (card) => {
    const amt = parseFloat(form.amount) || remaining
    if (!(amt > 0)) {
      setError('Enter the amount to charge first')
      return
    }
    const ok = window.confirm(
      `Charge ${brandLabel(card.brand)} ending in ${card.last4} for $${amt.toFixed(2)}?`
    )
    if (!ok) return
    setError('')
    setChargingId(card.id)
    const chargingCardLast4 = card.last4
    try {
      const r = await customerCardsAPI.charge(job.customer_id, {
        amount: Math.round(amt * 100),
        paymentMethodId: card.id,
        description: `Job #${job.id}`,
        invoiceId: job.invoice_id || null,
        jobId: job.id,
      })
      if (r.status === 'requires_action') {
        setError("The customer's bank requires authentication (3DS). Ask the customer to approve, then retry.")
        setChargingId(null)
        return
      }
      if (r.status !== 'succeeded') {
        setError(`Charge status: ${r.status}`)
        setChargingId(null)
        return
      }
      onClose?.()
      await onRecorded?.()
      const charged = `Charged $${amt.toFixed(2)} to card ending ${chargingCardLast4 || ''}.`
      if (r.recordWarning) {
        toast(`${charged}\nBut it could not be recorded in ServiceFlow: ${r.recordWarning}\nPlease contact support with payment ${r.paymentIntentId}.`, 'error', 12000)
      } else if (r.paymentState?.state === 'paid') {
        toast(`${charged}\nInvoice is now paid.`, 'success')
      } else {
        const left = Math.max(0, (r.paymentState?.due || 0) - (r.paymentState?.totalPaid || 0))
        toast(`${charged}\nPartially paid — $${left.toFixed(2)} still due.`, 'warning')
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Charge failed')
    } finally {
      setChargingId(null)
    }
  }

  const handleSubmit = async () => {
    if (!(paidAmount > 0)) return setError('Enter a payment amount')
    if (form.paymentMethod === 'other' && !form.customPaymentMethod.trim()) {
      return setError('Enter the payment method name')
    }
    setSaving(true)
    setError('')
    try {
      const { data: rec } = await api.post('/transactions/record-payment', {
        jobId: job.id,
        invoiceId: job.invoice_id || null,
        customerId: job.customer_id || null,
        amount: paidAmount,
        tipAmount: isAutoTip ? autoTip : (parseFloat(form.tipAmount) || 0),
        paymentMethod: methodKey,
        paymentDate: form.paymentDate,
        notes: form.notes || null,
      })
      const st = rec?.paymentState
      if (st?.state === 'paid') toast(`Payment of $${paidAmount.toFixed(2)} recorded. Invoice is now paid.`, 'success')
      else if (st) toast(`Payment of $${paidAmount.toFixed(2)} recorded. $${Math.max(0, st.due - st.totalPaid).toFixed(2)} still due.`, 'warning')
      else toast(`Payment of $${paidAmount.toFixed(2)} recorded.`, 'success')

      if (form.paymentMethod === 'other') {
        const name = form.customPaymentMethod.trim()
        if (!customMethods.some((pm) => String(pm.name).toLowerCase() === name.toLowerCase())) {
          paymentMethodsAPI.createPaymentMethod({ name }).catch(() => {})
        }
      }

      onClose?.()
      await onRecorded?.()
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to record payment')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-bold text-[var(--sf-ink)]">Collect payment</h3>
          <button onClick={onClose} className="text-[var(--sf-ink-3)] hover:text-[var(--sf-ink)] p-1" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-[12px] text-[var(--sf-ink-3)] mb-4">
          Total ${totalDue.toFixed(2)} · Paid ${alreadyPaid.toFixed(2)} · Due ${remaining.toFixed(2)}
        </p>

        {/* Saved cards on file for this customer (issue #70). Charges go
            through the tenant's connected Stripe account. If no cards yet,
            the operator can add one from Customer → Invoices tab. */}
        {cardsLoading && (
          <div className="mb-4 flex items-center gap-2 text-[12px] text-[var(--sf-ink-3)]">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Checking cards on file…
          </div>
        )}
        {!cardsLoading && savedCards.length > 0 && (
          <div className="mb-4 border border-[var(--sf-border-light)] rounded-lg p-3 bg-[var(--sf-bg-page)]">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-3.5 h-3.5 text-[var(--sf-ink-3)]" />
              <div className="text-[11.5px] font-semibold text-[var(--sf-ink-2)] uppercase tracking-wider">
                Charge saved card
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {savedCards.map((card) => (
                <div
                  key={card.id}
                  className="flex items-center gap-2 bg-white border border-[var(--sf-border-light)] rounded-md px-2.5 py-2"
                >
                  <CreditCard className="w-3.5 h-3.5 text-[var(--sf-ink-3)] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold text-[var(--sf-ink)]">
                      {brandLabel(card.brand)} •••• {card.last4}
                      {card.isDefault && (
                        <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--sf-blue-500)]">Default</span>
                      )}
                    </div>
                    <div className="text-[10.5px] text-[var(--sf-ink-3)]">
                      Expires {String(card.expMonth).padStart(2, '0')}/{String(card.expYear).slice(-2)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleChargeSaved(card)}
                    disabled={chargingId !== null || !(parseFloat(form.amount) > 0 || remaining > 0)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[11.5px] font-semibold text-white bg-[var(--sf-blue-500)] rounded-md hover:opacity-90 disabled:opacity-50"
                  >
                    {chargingId === card.id && <Loader2 className="w-3 h-3 animate-spin" />}
                    {chargingId === card.id ? 'Charging…' : 'Charge'}
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-2 text-[10.5px] text-[var(--sf-ink-3)]">
              Charges the amount in the field below. Add or manage cards from Customer → Invoices.
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-[12px] font-semibold text-[var(--sf-ink-2)] mb-1">Payment method</label>
            <select
              value={form.paymentMethod}
              onChange={(e) => setField('paymentMethod', e.target.value)}
              className={inputClass}
            >
              <option value="cash">Cash</option>
              <option value="check">Check</option>
              <option value="credit_card">Credit Card</option>
              <option value="bank_transfer">Bank Transfer</option>
              {customMethods.map((pm) => (
                <option key={pm.id} value={pm.name}>{pm.name}</option>
              ))}
              <option value="other">Other</option>
            </select>
            {form.paymentMethod === 'other' && (
              <input
                type="text"
                value={form.customPaymentMethod}
                onChange={(e) => setField('customPaymentMethod', e.target.value)}
                className={`${inputClass} mt-2`}
                placeholder="Payment method name"
                autoFocus
              />
            )}
          </div>

          <div>
            <label className="block text-[12px] font-semibold text-[var(--sf-ink-2)] mb-1">Amount</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.amount}
              onChange={(e) => setField('amount', e.target.value)}
              className={inputClass}
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="block text-[12px] font-semibold text-[var(--sf-ink-2)] mb-1">
              Tip
              <span className="font-normal text-[var(--sf-ink-3)] ml-1">
                ({isAutoTip ? 'auto: anything over the amount due' : 'manual'}, goes to payroll)
              </span>
            </label>
            {isAutoTip ? (
              <div className={`${inputClass} bg-[var(--sf-bg-page)] text-[var(--sf-ink-2)]`}>
                ${autoTip.toFixed(2)}
                {feePercent > 0 && (
                  <span className="text-[11px] text-[var(--sf-ink-3)] ml-2">
                    (fee {feePercent}% = ${fee.toFixed(2)})
                  </span>
                )}
              </div>
            ) : (
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.tipAmount}
                onChange={(e) => setField('tipAmount', e.target.value)}
                className={inputClass}
                placeholder="0.00"
              />
            )}
          </div>

          <div>
            <label className="block text-[12px] font-semibold text-[var(--sf-ink-2)] mb-1">Payment date</label>
            <input
              type="date"
              value={form.paymentDate}
              onChange={(e) => setField('paymentDate', e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-[12px] font-semibold text-[var(--sf-ink-2)] mb-1">Notes</label>
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              className={inputClass}
              placeholder="Optional"
            />
          </div>
        </div>

        {error && <p className="text-[12px] text-red-600 mt-3">{error}</p>}

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-[var(--sf-ink-2)] hover:text-[var(--sf-ink)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm font-semibold text-white bg-[var(--sf-blue-500)] rounded-lg hover:opacity-90 disabled:opacity-50"
          >
            {saving ? 'Recording...' : 'Record payment'}
          </button>
        </div>
      </div>
    </div>
  )
}
